import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Plus, Edit, Trash2, Tag as TagIcon, Loader2, ToggleLeft, ToggleRight 
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { TAG_COLORS } from '../components/TagComponents';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TagsPage() {
  const { hasPermission, role } = usePermissions();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    color: TAG_COLORS[0]
  });
  
  const canManageTags = ['admin', 'owner', 'sales_manager'].includes(role);
  
  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tags?include_inactive=${showInactive}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      logger.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchTags();
    
    // Listen for tags-updated event
    const handler = () => fetchTags();
    window.addEventListener('tags-updated', handler);
    return () => window.removeEventListener('tags-updated', handler);
  }, [fetchTags]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Tag created successfully');
        setIsCreateOpen(false);
        setFormData({ name: '', color: TAG_COLORS[0] });
        fetchTags();
        window.dispatchEvent(new CustomEvent('tags-updated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create tag');
      }
    } catch (error) {
      logger.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    } finally {
      setSaving(false);
    }
  };
  
  const handleEdit = async () => {
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tags/${selectedTag.tag_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Tag updated successfully');
        setIsEditOpen(false);
        setSelectedTag(null);
        fetchTags();
        window.dispatchEvent(new CustomEvent('tags-updated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update tag');
      }
    } catch (error) {
      logger.error('Error updating tag:', error);
      toast.error('Failed to update tag');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tags/${selectedTag.tag_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Tag deactivated successfully');
        setIsDeleteOpen(false);
        setSelectedTag(null);
        fetchTags();
        window.dispatchEvent(new CustomEvent('tags-updated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to deactivate tag');
      }
    } catch (error) {
      logger.error('Error deactivating tag:', error);
      toast.error('Failed to deactivate tag');
    } finally {
      setSaving(false);
    }
  };
  
  const handleReactivate = async (tag) => {
    try {
      const response = await fetch(`${API_URL}/api/tags/${tag.tag_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: true })
      });
      
      if (response.ok) {
        toast.success('Tag reactivated');
        fetchTags();
        window.dispatchEvent(new CustomEvent('tags-updated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reactivate tag');
      }
    } catch (error) {
      logger.error('Error reactivating tag:', error);
      toast.error('Failed to reactivate tag');
    }
  };
  
  const openEditDialog = (tag) => {
    setSelectedTag(tag);
    setFormData({ name: tag.name, color: tag.color });
    setIsEditOpen(true);
  };
  
  const openDeleteDialog = (tag) => {
    setSelectedTag(tag);
    setIsDeleteOpen(true);
  };
  
  if (!canManageTags) {
    return (
      <div className="space-y-6" data-testid="tags-page">
        <h1 className="text-2xl font-heading font-semibold text-slate-900">Tags</h1>
        <Card>
          <CardContent className="py-8 text-center">
            <TagIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">You do not have permission to manage tags.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="tags-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Tags</h1>
          <p className="text-sm text-slate-500 mt-1">Manage lead tags for organization and filtering</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} data-testid="create-tag-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Tag
        </Button>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-slate-300"
              />
              Show inactive tags
            </Label>
          </div>
        </CardContent>
      </Card>
      
      {/* Tags Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TagIcon className="h-5 w-5" />
            All Tags ({tags.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8">
              <TagIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No tags yet. Create your first tag to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tag</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map(tag => (
                  <TableRow key={tag.tag_id} className={!tag.is_active ? 'opacity-60' : ''}>
                    <TableCell>
                      <Badge
                        className="text-sm px-3 py-1 font-medium border"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          borderColor: tag.color,
                          color: tag.color
                        }}
                      >
                        {tag.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-xs text-slate-500 font-mono">{tag.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tag.is_active ? 'default' : 'secondary'}>
                        {tag.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(tag.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(tag)}
                          data-testid={`edit-tag-${tag.tag_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {tag.is_active ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDeleteDialog(tag)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-tag-${tag.tag_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleReactivate(tag)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <ToggleRight className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create Tag Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="tag-name">Tag Name</Label>
              <Input
                id="tag-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., VIP, Hot Lead, Follow-up"
                data-testid="tag-name-input"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="grid grid-cols-7 gap-2 mt-2">
                {TAG_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Preview</Label>
              <div className="mt-2 p-4 bg-slate-50 rounded-lg">
                <Badge
                  className="text-sm px-3 py-1 font-medium border"
                  style={{
                    backgroundColor: `${formData.color}20`,
                    borderColor: formData.color,
                    color: formData.color
                  }}
                >
                  {formData.name || 'Tag name'}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-tag-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Tag Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-tag-name">Tag Name</Label>
              <Input
                id="edit-tag-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="grid grid-cols-7 gap-2 mt-2">
                {TAG_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Preview</Label>
              <div className="mt-2 p-4 bg-slate-50 rounded-lg">
                <Badge
                  className="text-sm px-3 py-1 font-medium border"
                  style={{
                    backgroundColor: `${formData.color}20`,
                    borderColor: formData.color,
                    color: formData.color
                  }}
                >
                  {formData.name || 'Tag name'}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate the tag "{selectedTag?.name}"?
              <br /><br />
              Deactivated tags will no longer appear in the tag selection dropdown. 
              Existing leads with this tag will still display it (marked as inactive).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
